/** Structural mirror of the RPA plugin's `AxSelector` binding (selectors stay opaque wire JSON). */
type AxSelector = {
  role?: string;
  name?: string;
  matchMode: 'exact' | 'contains';
  automationId?: string;
  within?: AxSelector;
};

/**
 * The structured mapping payload, mirroring ml-scribe's `structured_contract.py`:
 * "if the note reads like case X, put this control in state Y". Carries no
 * identity of its own — template/section/app are the row's columns.
 */

export interface StructuredSourceCase {
  /** Referenced by the target's `states` keys. */
  id: string;
  /** What the clinician sees, and what the note text is matched against. */
  label: string;
}

export interface StructuredSource {
  kind: 'categorical';
  cases: StructuredSourceCase[];
}

export interface CheckboxTarget {
  kind: 'checkbox';
  selector: AxSelector;
  states: Record<string, { value: boolean }>;
}

export interface RadioTarget {
  kind: 'radio';
  /** Session-stable group token. Selecting one option deselects its siblings,
   *  so two mappings driving one group conflict — only detectable if stored. */
  groupId: string;
  states: Record<string, { selector: AxSelector }>;
}

export interface DropdownTarget {
  kind: 'dropdown';
  selector: AxSelector;
  states: Record<string, { value: string }>;
}

export type StructuredTarget = CheckboxTarget | RadioTarget | DropdownTarget;

export interface StructuredMapping {
  version: 1;
  source: StructuredSource;
  target: StructuredTarget;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/** Absent → undefined; usable → string; present-but-unusable → null (corrupt). */
const optionalString = (value: unknown): string | null | undefined => {
  if (value === undefined || value === null) return undefined;
  return nonEmptyString(value);
};

/**
 * ONE rule for every field: present must parse usable, or the selector is
 * refused — a dropped constraint widens the write onto a same-named sibling.
 */
const parseSelector = (value: unknown, depth = 0): AxSelector | null => {
  // A `within` chain is an ancestor walk, not a graph; a payload deep enough to
  // need this bound is malformed, not merely unusual.
  if (depth > 8 || !isRecord(value)) return null;
  const role = optionalString(value.role);
  const name = optionalString(value.name);
  const automationId = optionalString(value.automationId);
  if (role === null || name === null || automationId === null) return null;
  // The role/name anchor is mandatory even beside an `automationId` — an
  // id-only selector has nothing to cross-validate a reassigned id against.
  if (!role && !name) return null;
  const mode = value.matchMode;
  if (mode !== 'exact' && mode !== 'contains') return null;
  let within: AxSelector | undefined;
  if (value.within !== undefined && value.within !== null) {
    const parsed = parseSelector(value.within, depth + 1);
    if (!parsed) return null;
    within = parsed;
  }
  return {
    ...(role ? { role } : {}),
    ...(name ? { name } : {}),
    matchMode: mode,
    ...(automationId ? { automationId } : {}),
    ...(within ? { within } : {}),
  };
};

/** Read `states` as a map, requiring every entry to parse. */
const parseStates = <T>(
  states: unknown,
  declared: ReadonlySet<string>,
  parseState: (value: unknown) => T | null
): Record<string, T> | null => {
  if (!isRecord(states)) return null;
  // Null prototype: a declared "constructor" case must not read as present.
  const parsed: Record<string, T> = Object.create(null) as Record<string, T>;
  for (const [caseId, raw] of Object.entries(states)) {
    // A state naming a case the source never declared can only ever be dead
    // weight; the backend rejects it on save, so it means the row is corrupt.
    if (!declared.has(caseId)) return null;
    const state = parseState(raw);
    if (state === null) return null;
    parsed[caseId] = state;
  }
  return Object.keys(parsed).length > 0 ? parsed : null;
};

const parseTarget = (
  target: unknown,
  declared: ReadonlySet<string>
): StructuredTarget | null => {
  if (!isRecord(target)) return null;
  switch (target.kind) {
    case 'checkbox': {
      const selector = parseSelector(target.selector);
      const states = parseStates(target.states, declared, (value) =>
        isRecord(value) && typeof value.value === 'boolean'
          ? { value: value.value }
          : null
      );
      return selector && states ? { kind: 'checkbox', selector, states } : null;
    }
    case 'radio': {
      const groupId = nonEmptyString(target.groupId);
      const states = parseStates(target.states, declared, (value) => {
        const selector = isRecord(value) ? parseSelector(value.selector) : null;
        return selector ? { selector } : null;
      });
      return groupId && states ? { kind: 'radio', groupId, states } : null;
    }
    case 'dropdown': {
      const selector = parseSelector(target.selector);
      const states = parseStates(target.states, declared, (value) => {
        const option = isRecord(value) ? nonEmptyString(value.value) : null;
        return option ? { value: option } : null;
      });
      return selector && states ? { kind: 'dropdown', selector, states } : null;
    }
    default:
      return null;
  }
};

/**
 * Strict about every field the write dereferences: the alternative is a mapping
 * that parses now and misbehaves at apply time in front of a patient.
 */
export const parseStructuredMapping = (value: unknown): StructuredMapping | null => {
  if (!isRecord(value) || value.version !== 1) return null;

  const source = value.source;
  if (!isRecord(source) || source.kind !== 'categorical') return null;
  if (!Array.isArray(source.cases) || source.cases.length === 0) return null;
  const cases: StructuredSourceCase[] = [];
  const declared = new Set<string>();
  for (const entry of source.cases) {
    if (!isRecord(entry)) return null;
    const id = nonEmptyString(entry.id);
    const label = nonEmptyString(entry.label);
    if (!id || !label) return null;
    if (declared.has(id)) return null;
    declared.add(id);
    cases.push({ id, label });
  }

  const target = parseTarget(value.target, declared);
  if (!target) return null;

  return { version: 1, source: { kind: 'categorical', cases }, target };
};

/**
 * The cases this mapping can actually drive — one with no state does nothing.
 * Deciding WHICH of them a note section supports lives with the apply path;
 * this module only says what could be written.
 */
export const drivableCases = (mapping: StructuredMapping): StructuredSourceCase[] =>
  mapping.source.cases.filter((entry) => entry.id in mapping.target.states);
